// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';

import { DimensionsTypeChange, DataTypeChange } from 'flow/service/actions/matrixModels';
import type { FormState } from 'flow/service/actions/matrixModels';
import type { MatrixModel, OutputValueRange } from 'flow/store/engine/types';
import type { NodeEditorFormAdapter } from '../useNodeEditorForm';

import {
  DEFAULT_INIT_AXIS,
  DEFAULT_NULL_X_AXIS,
  getDataTypeOptions, getDimensionOptions, isMatrix1Dimensional,
} from '~/dashboard/views/workflows/service/actions';
import { buildIndexableActionOptionEntry } from '~/dashboard/views/workflows/store/engine/create/actions/helpers';


export function buildDataTypeOption(
  value: MatrixModel['matrixDataType'],
): Option {
  const options = getDataTypeOptions();
  return options.find(option => option.value === value);
}

export function buildDimensionOption(value: '1' | '2'): Option {
  const options = getDimensionOptions();
  return options.find(option => option.value === value);
}

export function getDataTypeChange(
  selectedDataTypeOption: Option, originalDataType: MatrixModel['matrixDataType'],
): DataTypeChange {
  if (selectedDataTypeOption.value === 'number' && originalDataType === 'string') {
    return DataTypeChange.CHANGE_TO_NUMBER;
  }
  if (selectedDataTypeOption.value === 'string' && originalDataType === 'number') {
    return DataTypeChange.CHANGE_TO_STRING;
  }
  return DataTypeChange.NO_CHANGE;
}

export function getDimensionsTypeChange(
  selectedDimensionOption: Option, xAxis: MatrixModel['axis']['x'],
): DimensionsTypeChange {
  const isMatrixOriginally1D = isMatrix1Dimensional(xAxis);
  if (selectedDimensionOption.value === '2' && isMatrixOriginally1D) {
    return DimensionsTypeChange.CHANGE_TO_2D;
  }
  if (selectedDimensionOption.value === '1' && !isMatrixOriginally1D) {
    return DimensionsTypeChange.CHANGE_TO_1D;
  }
  return DimensionsTypeChange.NO_CHANGE;
}

export const matrixOptionsFormAdapter: NodeEditorFormAdapter<FormState> = {
  getDefaultFormValues(action) {
    const { matrixModel, options } = action;

    return {
      name: matrixModel.name,
      description: (options.description?.value as string) ?? '',
      dataTypeOption: buildDataTypeOption(matrixModel.matrixDataType),
      dimensionsOption: isMatrix1Dimensional(matrixModel.axis.x)
        ? buildDimensionOption('1') : buildDimensionOption('2'),
      defineValueRanges: (
        (options?.output_value_range?.jsonValue ?? []) as OutputValueRange[]
      ).length > 0,
    };
  },

  mapFormDataToAction(data, sourceAction) {
    const { matrixModel, options } = sourceAction;

    const descriptionActonOption = options.description
      ? { description: { ...options.description, value: data.description ?? '' } }
      : buildIndexableActionOptionEntry( // in the case, MM was created in legacy
        'description', { value: data.description ?? '' },
      );

    const outputValueRangeActionOption = options.output_value_range
      ? { output_value_range: options.output_value_range }
      : buildIndexableActionOptionEntry(
        'output_value_range', { value: [], jsonValue: [] },
      );

    let xAxis = matrixModel.axis.x;
    let updatedDataRouteX = matrixModel.dataRouteX;
    let updatedMatrix = matrixModel.matrix;

    const dimensionsTypeChange = getDimensionsTypeChange(
      data.dimensionsOption, matrixModel.axis.x,
    );

    if (dimensionsTypeChange === DimensionsTypeChange.CHANGE_TO_1D) {
      // remove dataRouteX
      updatedDataRouteX = null;
      // reset xAxis as null
      xAxis = DEFAULT_NULL_X_AXIS;
      // make sure matrix only has 1 column (inner array length of 1)
      updatedMatrix = matrixModel.matrix.map(innerArray => innerArray.slice(0, 1));
    }

    if (dimensionsTypeChange === DimensionsTypeChange.CHANGE_TO_2D) {
      xAxis = DEFAULT_INIT_AXIS;
    }

    const dataTypeChange = getDataTypeChange(
      data.dataTypeOption, matrixModel.matrixDataType,
    );
    if (dataTypeChange !== DataTypeChange.NO_CHANGE) {
      updatedMatrix = updatedMatrix.map(innerArray => innerArray.map(() => null));
    }

    return {
      ...sourceAction,
      matrixModel: {
        ...matrixModel,
        name: data.name,
        matrixDataType: data.dataTypeOption.value,
        dataRouteX: updatedDataRouteX,
        axis: {
          ...matrixModel.axis,
          x: xAxis,
        },
        matrix: updatedMatrix,
        ...(typeof data.defineValueRanges === 'boolean' && {
          defineValueRanges: data.defineValueRanges,
        }),
      },
      options: {
        ...options,
        ...descriptionActonOption,
        ...outputValueRangeActionOption,
      },
    };
  },
};
